import React, { useState, useRef, useCallback } from 'react';
import {
    Box, Typography, Stack, Button, IconButton, Tooltip, CircularProgress, Chip,
    Snackbar, Alert, LinearProgress, Grid,
} from '@mui/material';
import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded';
import UploadFileRoundedIcon from '@mui/icons-material/UploadFileRounded';
import DescriptionRoundedIcon from '@mui/icons-material/DescriptionRounded';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import ErrorOutlineRoundedIcon from '@mui/icons-material/ErrorOutlineRounded';
import PlaylistAddCheckRoundedIcon from '@mui/icons-material/PlaylistAddCheckRounded';
import GroupAddRoundedIcon from '@mui/icons-material/GroupAddRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import RestartAltRoundedIcon from '@mui/icons-material/RestartAltRounded';
import InfoRoundedIcon from '@mui/icons-material/InfoRounded';
import FileDownloadRoundedIcon from '@mui/icons-material/FileDownloadRounded';
import { useNavigate, Navigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { selectUserTypeId, USER_TYPE } from '../redux/slices/authSlice';
import http, { apiErrorMessage } from '../Api/http';
import { GetImportTemplate, ValidateImport, ImportEmployees } from '../Api/Api';
import { fetchFileBlob, saveBlob, blobErrorMessage } from '../utils/download';

const PRIMARY = '#7C5CFC';
const PRIMARY_LIGHT = '#F1EEFE';
const PRIMARY_BORDER = '#C9BEFB';
const PRIMARY_DARK = '#6246E0';
const INK = '#0F172A';
const card = { bgcolor: '#fff', border: '1px solid #E6EAF1', borderRadius: '14px', boxShadow: '0 1px 3px rgba(16,24,40,0.06)' };
const solidBtn = { bgcolor: PRIMARY, color: '#fff', fontWeight: 700, borderRadius: '9px', boxShadow: 'none', textTransform: 'none', '&:hover': { bgcolor: PRIMARY_DARK }, '&.Mui-disabled': { bgcolor: '#E2E8F0', color: '#94A3B8' } };
const ghostBtn = { color: '#475569', bgcolor: '#fff', border: '1px solid #E5E7EB', fontWeight: 700, borderRadius: '9px', textTransform: 'none', '&:hover': { bgcolor: '#F8FAFC' } };

const XLSX_MIME = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
const isXlsx = (f) => f && (/\.xlsx$/i.test(f.name) || f.type === XLSX_MIME);
const fmtSize = (b) => (b > 1e6 ? `${(b / 1e6).toFixed(1)} MB` : `${Math.max(1, Math.round(b / 1024))} KB`);

const TEMPLATE_RULES = [
    'One employee per row on the "Employees" sheet, starting at row 2.',
    'Don’t rename, reorder or delete the header row (row 1).',
    'Only First Name, Personal Email and Personal Mobile (marked required) must be filled — everything else is optional and can be left blank.',
    'Dates use dd-MM-yyyy format, e.g. 05-08-1998.',
    'When filled in, Gender, Marital Status, Blood Group and Employment Type must be picked from the dropdown.',
    'Department and Designation are free text on import.',
    'If Employment Type is "Probation", Probation Period (Months) becomes required.',
    'Personal Email must be unique — not used by an existing employee, and not repeated in the file.',
    'Don’t add an Employee Code column — codes are generated automatically on import.',
    'Up to 1,000 employees per file.',
];

function ColHeader({ n, title, sub }) {
    return (
        <Stack direction="row" spacing={1.4} sx={{ alignItems: 'center', mb: 2 }}>
            <Box sx={{ width: 30, height: 30, borderRadius: '9px', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 800, fontSize: 14, bgcolor: PRIMARY_LIGHT, color: PRIMARY, border: `1px solid ${PRIMARY_BORDER}` }}>{n}</Box>
            <Box>
                <Typography sx={{ fontSize: 15, fontWeight: 800, color: INK, lineHeight: 1.15 }}>{title}</Typography>
                {sub && <Typography sx={{ fontSize: 12, color: '#94A3B8' }}>{sub}</Typography>}
            </Box>
        </Stack>
    );
}

export default function BulkImportEmployeesPage() {
    const navigate = useNavigate();
    const fileRef = useRef(null);
    const userTypeId = useSelector(selectUserTypeId);
    const allowed = userTypeId === USER_TYPE.MASTER_ADMIN || userTypeId === USER_TYPE.ADMIN;

    const [downloading, setDownloading] = useState(false);
    const [file, setFile] = useState(null);
    const [dragOver, setDragOver] = useState(false);

    const [validating, setValidating] = useState(false);
    const [validation, setValidation] = useState(null);

    const [importing, setImporting] = useState(false);
    const [result, setResult] = useState(null);

    const [snack, setSnack] = useState(null);
    const notify = (msg, sev = 'success') => setSnack({ msg, sev });

    const downloadTemplate = async () => {
        setDownloading(true);
        try {
            const { blob, name } = await fetchFileBlob(GetImportTemplate);
            saveBlob(blob, name || 'employee-import-template.xlsx');
        } catch (err) {
            notify(await blobErrorMessage(err, 'Could not download the template.'), 'error');
        } finally {
            setDownloading(false);
        }
    };

    const validate = useCallback(async (f) => {
        setValidating(true);
        setValidation(null);
        setResult(null);
        try {
            const fd = new FormData();
            fd.append('file', f);
            const { data: body } = await http.post(ValidateImport, fd);
            if (body?.error && !body?.data) throw new Error(body.message || 'Could not validate the file.');
            const d = body?.data || body || {};
            setValidation({
                totalRows: Number(d.totalRows) || 0,
                validRows: Number(d.validRows) || 0,
                canImport: Boolean(d.canImport),
                errors: Array.isArray(d.errors) ? d.errors : [],
            });
        } catch (err) {
            notify(apiErrorMessage(err, 'Could not validate the file.'), 'error');
        } finally {
            setValidating(false);
        }
    }, []);

    const acceptFile = (f) => {
        if (!f) return;
        if (!isXlsx(f)) { notify('Please choose the .xlsx template file — other formats are not accepted.', 'warning'); return; }
        setFile(f);
        setResult(null);
        validate(f);
    };

    const onPick = (e) => { acceptFile(e.target.files?.[0]); e.target.value = ''; };
    const onDrop = (e) => { e.preventDefault(); setDragOver(false); acceptFile(e.dataTransfer.files?.[0]); };
    const reset = () => { setFile(null); setValidation(null); setResult(null); };

    const runImport = async () => {
        if (!file) return;
        setImporting(true);
        try {
            const fd = new FormData();
            fd.append('file', file);
            const { data: body } = await http.post(ImportEmployees, fd);
            if (body?.error && !body?.data) throw new Error(body.message || 'Import failed.');
            const d = body?.data || body || {};
            setResult({ imported: Number(d.imported) || 0, employeeCodes: Array.isArray(d.employeeCodes) ? d.employeeCodes : [] });
            notify(`${Number(d.imported) || 0} employee(s) imported.`);
        } catch (err) {
            const errs = err?.response?.data?.errors;
            if (Array.isArray(errs) && errs.length) {
                setValidation((v) => ({ totalRows: v?.totalRows || 0, validRows: v?.validRows || 0, canImport: false, errors: errs }));
                notify('Some rows are invalid — fix them and re-upload.', 'error');
            } else {
                notify(apiErrorMessage(err, 'Import failed.'), 'error');
            }
        } finally {
            setImporting(false);
        }
    };

    const clean = validation && validation.canImport && validation.errors.length === 0;

    if (!allowed) return <Navigate to="/dashboard/employees" replace />;

    return (
        <Box sx={{ px: 2, pb:2, pt:1 }}>
            <Stack direction="row" spacing={1.4} sx={{ alignItems: 'center', mb: 2 }}>
                <IconButton onClick={() => navigate('/dashboard/employees')} sx={{ width: 40, height: 40, borderRadius: '10px', bgcolor: '#fff', border: '1px solid #E6EAF1', '&:hover': { bgcolor: PRIMARY_LIGHT } }}>
                    <ArrowBackRoundedIcon sx={{ fontSize: 19, color: '#334155' }} />
                </IconButton>
                <Box>
                    <Typography sx={{ fontSize: 22, fontWeight: 800, color: INK, letterSpacing: '-0.4px' }}>Bulk Import Employees</Typography>
                    <Typography sx={{ fontSize: 13, color: '#6B7280', mt: 0.2 }}>Onboard up to 1,000 employees at once from a single Excel file.</Typography>
                </Box>
            </Stack>

            {result ? (
                <Box sx={{ ...card, p: 4, textAlign: 'center', maxWidth: 620, mx: 'auto' }}>
                    <Box sx={{ width: 64, height: 64, borderRadius: '50%', bgcolor: '#DCFCE7', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', mb: 1.6 }}>
                        <CheckCircleRoundedIcon sx={{ fontSize: 36, color: '#16A34A' }} />
                    </Box>
                    <Typography sx={{ fontSize: 20, fontWeight: 800, color: INK }}>{result.imported} employee{result.imported === 1 ? '' : 's'} imported</Typography>
                    <Typography sx={{ fontSize: 13, color: '#64748B', mt: 0.6, maxWidth: 460, mx: 'auto', lineHeight: 1.6 }}>
                        Their Employee IDs were generated automatically. Set each one&apos;s login password and role from their profile before they can sign in.
                    </Typography>
                    {result.employeeCodes.length > 0 && (
                        <Stack direction="row" spacing={0.8} sx={{ flexWrap: 'wrap', gap: 0.8, justifyContent: 'center', mt: 2 }}>
                            {result.employeeCodes.slice(0, 24).map((c) => (
                                <Chip key={c} label={c} size="small" sx={{ height: 24, fontSize: 11.5, fontWeight: 700, bgcolor: PRIMARY_LIGHT, color: PRIMARY_DARK }} />
                            ))}
                            {result.employeeCodes.length > 24 && (
                                <Chip label={`+${result.employeeCodes.length - 24} more`} size="small" sx={{ height: 24, fontSize: 11.5, fontWeight: 700, bgcolor: '#F1F5F9', color: '#64748B' }} />
                            )}
                        </Stack>
                    )}
                    <Stack direction="row" spacing={1.2} sx={{ justifyContent: 'center', mt: 3 }}>
                        <Button onClick={() => navigate('/dashboard/employees')} sx={{ ...solidBtn, height: 42, px: 2.6 }}>Go to Employees</Button>
                        <Button onClick={reset} startIcon={<RestartAltRoundedIcon sx={{ fontSize: 18 }} />} sx={{ ...ghostBtn, height: 42, px: 2.2 }}>Import another file</Button>
                    </Stack>
                </Box>
            ) : (
                <>
                    <Stack direction="row" spacing={1.2} sx={{ alignItems: 'flex-start', mb: 1, p: 1.5, borderRadius: '11px', bgcolor: '#FFFBEB', border: '1px solid #FDE68A' }}>
                        <InfoRoundedIcon sx={{ fontSize: 18, color: '#B45309', flexShrink: 0, mt: 0.1 }} />
                        <Typography sx={{ fontSize: 12.5, color: '#78350F', lineHeight: 1.55 }}>
                            <strong>Download a fresh template every time you import.</strong> Fill in that exact file and upload it back — don&apos;t reuse an older download or change the columns.
                        </Typography>
                    </Stack>

                    <Grid container spacing={1.8} alignItems="stretch">
                        <Grid size={{ xs: 12, md: 5 }}>
                            <Box sx={{ ...card, p: { xs: 2, sm: 2.6 }, height: '100%', display: 'flex', flexDirection: 'column' }}>
                                <ColHeader n={1} title="Get the template" sub="Fill it in, keep the layout as-is" />

                                <Button
                                    onClick={downloadTemplate} disabled={downloading} fullWidth
                                    startIcon={downloading ? <CircularProgress size={17} sx={{ color: '#fff' }} /> : <FileDownloadRoundedIcon sx={{ fontSize: 20 }} />}
                                    sx={{ ...solidBtn, height: 46, fontSize: 13.5 }}
                                >
                                    {downloading ? 'Preparing…' : 'Download Excel template'}
                                </Button>

                                <Stack direction="row" spacing={0.7} sx={{ alignItems: 'center', mt: 2.4, mb: 1.2 }}>
                                    <PlaylistAddCheckRoundedIcon sx={{ fontSize: 17, color: PRIMARY }} />
                                    <Typography sx={{ fontSize: 12.5, fontWeight: 800, color: INK }}>Rules for filling the template</Typography>
                                </Stack>
                                <Box sx={{ flex: 1, borderRadius: '11px', bgcolor: '#F8FAFF', border: '1px solid #E8ECF6', p: 1.6, overflow: 'auto', maxHeight: { xs: 'none', md: 340 } }}>
                                    <Stack component="ol" spacing={1} sx={{ m: 0, pl: 0, listStyle: 'none' }}>
                                        {TEMPLATE_RULES.map((r, i) => (
                                            <Stack key={i} component="li" direction="row" spacing={1.1} sx={{ alignItems: 'flex-start' }}>
                                                <Box sx={{ width: 18, height: 18, borderRadius: '50%', flexShrink: 0, mt: 0.15, bgcolor: '#fff', border: `1px solid ${PRIMARY_BORDER}`, color: PRIMARY, fontSize: 10, fontWeight: 800, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{i + 1}</Box>
                                                <Typography sx={{ fontSize: 12.5, color: '#475569', lineHeight: 1.5 }}>{r}</Typography>
                                            </Stack>
                                        ))}
                                    </Stack>
                                </Box>
                            </Box>
                        </Grid>

                        <Grid size={{ xs: 12, md: 7 }}>
                            <Box sx={{ ...card, p: { xs: 2, sm: 2.6 }, height: '100%', display: 'flex', flexDirection: 'column' }}>
                                <ColHeader n={2} title="Upload & import" sub="Validated before anything is saved" />

                                <input ref={fileRef} type="file" accept=".xlsx" hidden onChange={onPick} />
                                {!file ? (
                                    <Box
                                        onClick={() => fileRef.current?.click()}
                                        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                                        onDragLeave={() => setDragOver(false)}
                                        onDrop={onDrop}
                                        sx={{
                                            border: `2px dashed ${dragOver ? PRIMARY : '#D8DEE9'}`, borderRadius: '12px',
                                            bgcolor: dragOver ? PRIMARY_LIGHT : '#FBFCFE', px: 3, py: 4, textAlign: 'center', cursor: 'pointer',
                                            transition: 'border-color .15s, background-color .15s', '&:hover': { borderColor: PRIMARY_BORDER, bgcolor: '#F8F7FE' },
                                        }}
                                    >
                                        <Box sx={{ width: 52, height: 52, borderRadius: '14px', bgcolor: PRIMARY_LIGHT, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', mb: 1.2 }}>
                                            <UploadFileRoundedIcon sx={{ fontSize: 26, color: PRIMARY }} />
                                        </Box>
                                        <Typography sx={{ fontSize: 14, fontWeight: 700, color: INK }}>Drag &amp; drop your .xlsx here</Typography>
                                        <Typography sx={{ fontSize: 12.5, color: '#94A3B8', mt: 0.3 }}>or <Box component="span" sx={{ color: PRIMARY, fontWeight: 700 }}>click to browse</Box> · Excel (.xlsx) only</Typography>
                                    </Box>
                                ) : (
                                    <Box>
                                        <Stack direction="row" spacing={1.4} sx={{ alignItems: 'center', p: 1.5, borderRadius: '11px', border: '1px solid #EEF0F6', bgcolor: '#F8FAFC' }}>
                                            <Box sx={{ width: 40, height: 40, borderRadius: '10px', bgcolor: '#DCFCE7', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                                                <DescriptionRoundedIcon sx={{ fontSize: 20, color: '#16A34A' }} />
                                            </Box>
                                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                                <Typography sx={{ fontSize: 13.5, fontWeight: 700, color: INK }} noWrap>{file.name}</Typography>
                                                <Typography sx={{ fontSize: 11.5, color: '#94A3B8' }}>{fmtSize(file.size)}</Typography>
                                            </Box>
                                            <Button onClick={() => validate(file)} disabled={validating} sx={{ ...ghostBtn, height: 34, px: 1.5, fontSize: 12 }}>Re-check</Button>
                                            <Tooltip arrow title="Choose a different file">
                                                <IconButton onClick={reset} size="small" sx={{ color: '#94A3B8', '&:hover': { color: '#E11D48', bgcolor: '#FEF2F2' } }}>
                                                    <CloseRoundedIcon sx={{ fontSize: 18 }} />
                                                </IconButton>
                                            </Tooltip>
                                        </Stack>
                                        {validating && <LinearProgress sx={{ mt: 1.4, borderRadius: 4, '& .MuiLinearProgress-bar': { bgcolor: PRIMARY } }} />}
                                    </Box>
                                )}

                                {validation && !validating && (
                                    <Box sx={{ mt: 1.8 }}>
                                        <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 1, mb: 1.4 }}>
                                            <Chip icon={<PlaylistAddCheckRoundedIcon sx={{ fontSize: '16px !important' }} />} label={`${validation.totalRows} row${validation.totalRows === 1 ? '' : 's'}`} size="small" sx={{ height: 26, fontSize: 12, fontWeight: 700, bgcolor: '#F1F5F9', color: '#475569', '& .MuiChip-icon': { color: '#64748B' } }} />
                                            <Chip label={`${validation.validRows} valid`} size="small" sx={{ height: 26, fontSize: 12, fontWeight: 800, bgcolor: '#DCFCE7', color: '#16A34A' }} />
                                            {validation.errors.length > 0 && (
                                                <Chip label={`${validation.errors.length} to fix`} size="small" sx={{ height: 26, fontSize: 12, fontWeight: 800, bgcolor: '#FEE2E2', color: '#DC2626' }} />
                                            )}
                                        </Stack>

                                        {clean ? (
                                            <Stack direction="row" spacing={1} sx={{ alignItems: 'center', p: 1.4, borderRadius: '10px', bgcolor: '#F0FDF4', border: '1px solid #BBF7D0' }}>
                                                <CheckCircleRoundedIcon sx={{ fontSize: 20, color: '#16A34A' }} />
                                                <Typography sx={{ fontSize: 13, fontWeight: 700, color: '#166534' }}>Every row looks good — you&apos;re ready to import.</Typography>
                                            </Stack>
                                        ) : (
                                            <Box sx={{ border: '1px solid #FECACA', borderRadius: '10px', overflow: 'hidden' }}>
                                                <Stack direction="row" spacing={1} sx={{ alignItems: 'center', px: 1.6, py: 1.1, bgcolor: '#FEF2F2', borderBottom: '1px solid #FECACA' }}>
                                                    <ErrorOutlineRoundedIcon sx={{ fontSize: 18, color: '#DC2626' }} />
                                                    <Typography sx={{ fontSize: 12.5, fontWeight: 800, color: '#B91C1C' }}>Fix these, then re-upload the file</Typography>
                                                </Stack>
                                                <Box sx={{ maxHeight: 220, overflow: 'auto' }}>
                                                    <Box component="table" sx={{ width: '100%', borderCollapse: 'collapse' }}>
                                                        <Box component="tbody">
                                                            {validation.errors.map((e, i) => (
                                                                <Box component="tr" key={i} sx={{ bgcolor: i % 2 ? '#FFFBFB' : '#fff' }}>
                                                                    <Box component="td" sx={{ px: 1.6, py: 0.9, fontSize: 12.5, fontWeight: 800, color: '#DC2626', borderBottom: '1px solid #F6EEEE', whiteSpace: 'nowrap', verticalAlign: 'top' }}>{e.row != null ? `Row ${e.row}` : '—'}</Box>
                                                                    <Box component="td" sx={{ px: 1.6, py: 0.9, fontSize: 12.5, fontWeight: 600, color: '#334155', borderBottom: '1px solid #F6EEEE', whiteSpace: 'nowrap', verticalAlign: 'top' }}>{e.field || '—'}</Box>
                                                                    <Box component="td" sx={{ px: 1.6, py: 0.9, fontSize: 12.5, color: '#475569', borderBottom: '1px solid #F6EEEE' }}>{e.message}</Box>
                                                                </Box>
                                                            ))}
                                                        </Box>
                                                    </Box>
                                                </Box>
                                            </Box>
                                        )}
                                    </Box>
                                )}

                                <Box sx={{ flexGrow: 1 }} />
                                <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid #F1F3F7' }}>
                                    <Button
                                        onClick={runImport} disabled={!clean || importing} fullWidth
                                        startIcon={importing ? <CircularProgress size={17} sx={{ color: '#fff' }} /> : <GroupAddRoundedIcon sx={{ fontSize: 20 }} />}
                                        sx={{ ...solidBtn, height: 48, fontSize: 14 }}
                                    >
                                        {importing ? 'Importing…' : validation ? `Import ${validation.validRows} employee${validation.validRows === 1 ? '' : 's'}` : 'Import employees'}
                                    </Button>
                                    <Typography sx={{ fontSize: 11.5, color: '#94A3B8', mt: 1, textAlign: 'center' }}>
                                        {clean ? 'All-or-nothing — every row is saved together.' : validation ? 'Resolve the issues above to enable import.' : 'Download, fill and upload the template to begin.'}
                                    </Typography>
                                </Box>
                            </Box>
                        </Grid>
                    </Grid>
                </>
            )}

            <Snackbar open={Boolean(snack)} autoHideDuration={4000} onClose={() => setSnack(null)} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
                {snack ? <Alert onClose={() => setSnack(null)} severity={snack.sev} sx={{ borderRadius: '9px', fontWeight: 600 }}>{snack.msg}</Alert> : undefined}
            </Snackbar>
        </Box>
    );
}
